import { toast } from 'react-toastify';

const baseOptions = {
  position: 'top-right',
  autoClose: 3500,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

function normalizeMessage(message, fallback) {
  if (typeof message === 'string' && message.trim()) return message;
  return fallback;
}

// O que o framework responde por conta própria: texto técnico, em inglês, que não ajuda quem está
// na tela. Só estas cedem lugar à frase do status — tudo o mais que o back escreveu é mantido.
const GENERICAS_DO_FRAMEWORK = [
  'the given data was invalid',
  'server error',
  'internal server error',
  'not found',
  'forbidden',
  'unauthorized',
  'unauthenticated',
  'bad request',
];

function translateApiMessage(message, status) {
  const raw = (message || '').toString().trim();
  if (!raw) return '';

  const normalized = raw.toLowerCase();

  if (normalized === 'unauthenticated user') {
    return 'Usuário ou senha inválidos.';
  }

  if (normalized.includes('method not allowed')) {
    return 'Método de requisição não permitido.';
  }

  if (normalized.includes('service unavailable')) {
    return 'Serviço temporariamente indisponível. Tente novamente em instantes.';
  }

  if (normalized.includes('network error')) {
    return 'Falha de conexão com o servidor.';
  }

  // A mensagem que o BACK escreveu vence a frase do status. Elas são específicas ("Malha não
  // encontrada.", "O campo origem é obrigatório.") e trocá-las por "Revise os campos" apagava a
  // única informação útil do erro. Só cedem lugar as frases que o framework devolve sozinho, que
  // não dizem nada a quem está na tela.
  if (!GENERICAS_DO_FRAMEWORK.some(g => normalized.includes(g))) return raw;

  if (status === 401) return 'Usuário ou senha inválidos.';
  if (status === 403) return 'Você não tem permissão para realizar esta ação.';
  if (status === 404) return 'Recurso não encontrado.';
  if (status === 422) return 'Dados inválidos. Revise os campos e tente novamente.';
  if (status === 500) return 'Erro interno no servidor. Tente novamente.';
  if (status === 503) return 'Serviço temporariamente indisponível. Tente novamente em instantes.';

  return raw;
}

function apiErrorMessage(error, fallback = 'Ocorreu um erro ao processar sua solicitação.') {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (Array.isArray(data?.errors)) {
    return translateApiMessage(data.errors[0], status) || fallback;
  }

  if (data?.errors && typeof data.errors === 'object') {
    const firstField = Object.keys(data.errors)[0];
    const firstFieldError = firstField ? data.errors[firstField]?.[0] : null;
    if (firstFieldError) return translateApiMessage(firstFieldError, status) || fallback;
  }

  const candidate =
    data?.mensagem ||
    data?.message ||
    data?.error ||
    error?.message ||
    '';

  return translateApiMessage(candidate, status) || fallback;
}

const notify = {
  success(message, options = {}) {
    return toast.success(normalizeMessage(message, 'Operação realizada com sucesso.'), {
      ...baseOptions,
      ...options,
    });
  },

  error(message, options = {}) {
    return toast.error(normalizeMessage(message, 'Ocorreu um erro ao processar sua solicitação.'), {
      ...baseOptions,
      ...options,
    });
  },

  apiError(error, fallback, options = {}) {
    return toast.error(apiErrorMessage(error, fallback), {
      ...baseOptions,
      ...options,
    });
  },

  info(message, options = {}) {
    return toast.info(normalizeMessage(message, 'Informação.'), {
      ...baseOptions,
      ...options,
    });
  },

  warning(message, options = {}) {
    return toast.warn(normalizeMessage(message, 'Atenção.'), {
      ...baseOptions,
      ...options,
    });
  },

  loading(message = 'Processando...', options = {}) {
    return toast.loading(message, {
      ...baseOptions,
      closeOnClick: false,
      autoClose: false,
      ...options,
    });
  },

  update(toastId, { message, type = 'default', isLoading = false, autoClose = 3500, ...rest } = {}) {
    return toast.update(toastId, {
      render: message,
      type,
      isLoading,
      autoClose,
      ...rest,
    });
  },

  dismiss(toastId) {
    return toast.dismiss(toastId);
  },
};

export default notify;
export { apiErrorMessage };
